// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

/**
 * Name Component - Refactored for Performance & Clean Code
 * 
 * Features:
 * - Memoized calculations and event handlers
 * - Optimized tooltip and rarity calculations
 * - Custom hook for outside click detection
 * - Proper accessibility support
 * - Clean state management
 * - Performance optimized with React best practices
 */

// Custom hook for outside click detection
const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [ref, callback]);
};

// Utility functions
const formatDate = (date) => {
  if (!date) return null;
  
  return (
    date.getFullYear().toString().slice(-2) +
    "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + date.getDate()).slice(-2) +
    " " +
    ("0" + date.getHours()).slice(-2) +
    ":" +
    ("0" + date.getMinutes()).slice(-2)
  );
};

const calculateDateRange = (item) => {
  const startDate = item?.startsOn ? new Date(item.startsOn * 1000) : null;
  const endDate = item?.endsOn ? new Date(item.endsOn * 1000) : null;
  
  return {
    startDay: formatDate(startDate),
    endDay: formatDate(endDate)
  };
};

const Name = memo(({
  nameList = [],
  setUserSelection,
  selectedItems = {},
  title = "Select Name",
  rarityList = [],
  webPrimaryColor = "#000"
}) => {
  // State management
  const [isListOpen, setIsListOpen] = useState(false);
  const [showRarityBox, setShowRarityBox] = useState(false);
  
  // Refs
  const dropdownRef = useRef(null);
  
  // Memoized current name display
  const currentName = useMemo(() => {
    return selectedItems?.name?.approvedName || "Pick a name";
  }, [selectedItems?.name?.approvedName]);

  // Memoized tooltip calculation
  const tooltipData = useMemo(() => {
    if (!selectedItems?.name?.approvedNameId || !rarityList.length) {
      return { text: "", hasRarity: false };
    }

    const nameRarity = rarityList.find(
      (rarity) => rarity.idOfType === selectedItems.name.approvedNameId
    );

    if (!nameRarity) {
      return { text: "", hasRarity: false };
    }

    const availableNfts = nameRarity.maxCount - nameRarity.currentCount;
    const totalAllowed = nameRarity.maxCount;
    const dates = calculateDateRange(nameRarity);
    
    let tooltipMsg = "Rarity of this Name:";
    
    if (dates.startDay && dates.endDay) {
      tooltipMsg += `<br>- Mint from ${dates.startDay} to ${dates.endDay}.`;
    } else if (dates.endDay) {
      tooltipMsg += `<br>- Minting ends on ${dates.endDay}.`;
    }
    
    if (availableNfts > 0) {
      tooltipMsg += `<br>- Only ${availableNfts} of ${totalAllowed} left.`;
    }

    return { text: tooltipMsg, hasRarity: true };
  }, [selectedItems?.name?.approvedNameId, rarityList]);

  // Event handlers with useCallback for performance
  const handleToggleList = useCallback(() => {
    setIsListOpen(prev => !prev);
  }, []);

  const handleCloseList = useCallback(() => {
    setIsListOpen(false);
  }, []);

  const handleNameChange = useCallback((item) => {
    if (!setUserSelection || !item) return;
    
    setUserSelection({ 
      name: { 
        ...item, 
        category: "approvedname" 
      } 
    });
    setIsListOpen(false);
  }, [setUserSelection]);

  const handleMouseEnter = useCallback(() => {
    if (tooltipData.hasRarity) {
      setShowRarityBox(true);
    }
  }, [tooltipData.hasRarity]);

  const handleMouseLeave = useCallback(() => {
    setShowRarityBox(false);
  }, []);

  // Custom hook usage
  useOutsideClick(dropdownRef, handleCloseList);

  // Memoized name list items
  const nameListItems = useMemo(() => {
    return nameList.map((item, index) => (
      <li
        key={item?.approvedNameId || index}
        className="nft-name__list"
        onClick={() => handleNameChange(item)}
        role="option"
        aria-selected={selectedItems?.name?.approvedNameId === item?.approvedNameId}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNameChange(item);
          }
        }}
      >
        {item?.approvedName || 'Unnamed'}
      </li>
    ));
  }, [nameList, selectedItems?.name?.approvedNameId, handleNameChange]);

  return (
    <div className="name-selection-container">
      <h3 className="mint__step-title">{title}</h3>
      
      <div
        className="nft-name__dropdown-wrapper"
        ref={dropdownRef}
        role="combobox"
        aria-expanded={isListOpen}
        aria-haspopup="listbox"
        aria-label="Name selection dropdown"
      >
        <div
          className="nft-name__dropdown-top"
          onClick={handleToggleList}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          role="button"
          tabIndex={0}
          aria-label={`Selected name: ${currentName}. Click to open options.`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleList();
            }
          }}
        >
          <div className="nft-name__dropdown-header">
            <span className="nft-name" title={currentName}>
              {currentName}
            </span>
          </div>
          
          <div 
            className="chevron-down"
            style={{ 
              transform: isListOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            <FontAwesomeIcon 
              icon={faChevronDown} 
              color={webPrimaryColor} 
              size="xl" 
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Dropdown List */}
        <div
          className={`nft-name__dropdown-bottom ${isListOpen ? "d-block" : "d-none"}`}
          role="listbox"
          aria-label="Available names"
        >
          <ul className="nft-name__lists">
            {nameListItems}
          </ul>
        </div>
      </div>

      {/* Rarity Tooltip */}
      {showRarityBox && tooltipData.hasRarity && (
        <div 
          className="rarity-box"
          role="tooltip"
          aria-live="polite"
        >
          <span
            className="rarity-text"
            dangerouslySetInnerHTML={{ __html: tooltipData.text }}
          />
        </div>
      )}
    </div>
  );
});

// Display name for debugging
Name.displayName = 'Name';

export default Name;
